// Unit conversion + compatibility helpers for recipes / inventory.
// Mirrors backend convert_qty (server.py).

const UNIT_BASE = {
  gr: { base: "gr", factor: 1, type: "mass" },
  kg: { base: "gr", factor: 1000, type: "mass" },
  ml: { base: "ml", factor: 1, type: "volume" },
  l: { base: "ml", factor: 1000, type: "volume" },
  pieza: { base: "pieza", factor: 1, type: "count" },
};

export function unitInfo(u) {
  return UNIT_BASE[(u || "").toLowerCase()] || { base: u, factor: 1, type: "unknown" };
}

export function unitsCompatible(a, b) {
  const ai = unitInfo(a);
  const bi = unitInfo(b);
  return ai.base === bi.base;
}

/** Convert qty from `fromUnit` to `toUnit`. Returns null if incompatible. */
export function convertQty(qty, fromUnit, toUnit) {
  const f = unitInfo(fromUnit);
  const t = unitInfo(toUnit);
  if (f.base !== t.base) return null;
  return (Number(qty) || 0) * f.factor / t.factor;
}

/** Real cost of an ingredient line:
 *  ing.cost_per_unit is priced per ing.unit (e.g. $150 per kg).
 *  line.quantity is in line.unit (e.g. 30 gr).
 *  → real cost = convertQty(line.qty, line.unit, ing.unit) * ing.cost_per_unit
 *  Returns { cost, compatible }. If incompatible we fall back to direct multiplication
 *  (same as backend) but mark compatible=false so the UI can warn.
 */
export function lineCost(line, ing) {
  if (!ing) return { cost: 0, compatible: false };
  const cpu = Number(ing.cost_per_unit || 0);
  if (!cpu) return { cost: 0, compatible: true };
  const qty = Number(line.quantity || 0);
  const converted = convertQty(qty, line.unit, ing.unit);
  if (converted === null) {
    return { cost: qty * cpu, compatible: false };
  }
  return { cost: converted * cpu, compatible: true };
}

/** Smart validations for a recipe line.
 *  Returns an array of { severity: "warn"|"error", message } */
export function validateRecipeLine(line, ing) {
  const issues = [];
  if (!ing) return issues;
  if (!unitsCompatible(line.unit, ing.unit)) {
    issues.push({
      severity: "error",
      message: `Verifica las unidades de medida: el ingrediente está en ${ing.unit} pero la receta usa ${line.unit}.`,
    });
  }
  const cpu = Number(ing.cost_per_unit || 0);
  // Sanity threshold based on unit type — heuristic, MXN
  const info = unitInfo(ing.unit);
  let threshold = null;
  if (info.type === "mass" && info.base === "gr") threshold = ing.unit === "kg" ? 5000 : 5;        // $5000/kg or $5/gr
  else if (info.type === "volume" && info.base === "ml") threshold = ing.unit === "l" ? 2000 : 2;  // $2000/l or $2/ml
  else if (info.type === "count") threshold = 2000;                                                // $2000/pieza
  if (threshold && cpu > threshold) {
    issues.push({
      severity: "warn",
      message: `El costo del ingrediente parece demasiado alto (${cpu.toFixed(2)} / ${ing.unit}). Verifica antes de guardar.`,
    });
  }
  if (Number(line.quantity) > 0 && cpu === 0) {
    issues.push({
      severity: "warn",
      message: "Este ingrediente no tiene costo unitario — el margen será 100%.",
    });
  }
  return issues;
}

export const UNIT_HINTS = {
  gr: "gramos · 1000 gr = 1 kg",
  kg: "kilogramos · 1 kg = 1000 gr",
  ml: "mililitros · 1000 ml = 1 l",
  l: "litros · 1 l = 1000 ml",
  pieza: "piezas / unidades enteras",
};

/** Compute the recommended price to reach a target margin (default 60%).
 *  Rounds up to the next $5 for nicer menu pricing.
 *  Returns null when there's no real cost (no recipe) or target is unrealistic. */
export function suggestedPrice(cost, targetMarginPct = 60) {
  const c = Number(cost || 0);
  if (c <= 0) return null;
  const t = Math.max(1, Math.min(95, Number(targetMarginPct))) / 100;
  const raw = c / (1 - t);            // price s.t. (price - cost) / price = t
  return Math.ceil(raw / 5) * 5;      // round up to nearest $5
}
